import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, Edit, Trash2, TrendingUp, TrendingDown, AlertCircle, RotateCcw, Download } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'voti', label: 'Voti' },
  { value: 'verifiche', label: 'Verifiche' },
  { value: 'professore', label: 'Professore' },
  { value: 'salute', label: 'Salute' },
];

export const BonusMalusTab = ({ leagueId, token, isAdmin, leagueName }) => {
  const [bonusMalus, setBonusMalus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form states for adding
  const [newName, setNewName] = useState('');
  const [newPoints, setNewPoints] = useState('');
  const [newCategory, setNewCategory] = useState('voti');
  const [newType, setNewType] = useState('bonus');
  
  // Form states for editing
  const [itemToEdit, setItemToEdit] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPoints, setEditPoints] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editType, setEditType] = useState('bonus');

  const fetchBonusMalus = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/bonus-malus`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setBonusMalus(data);
      }
    } catch (err) {
      console.error('Error fetching bonus/malus:', err);
    } finally {
      setIsLoading(false);
    }
  }, [leagueId, token]);

  useEffect(() => {
    fetchBonusMalus();
  }, [fetchBonusMalus]);

  const handleAddBonusMalus = async () => {
    if (!newName.trim() || !newPoints) {
      toast.error('Inserisci tutti i campi');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/bonus-malus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newName,
          points: parseInt(newPoints),
          category: newCategory,
          type: newType,
        }),
      });

      if (response.ok) {
        toast.success('Bonus/Malus aggiunto!');
        setShowAddDialog(false);
        setNewName('');
        setNewPoints('');
        setNewCategory('voti');
        setNewType('bonus');
        fetchBonusMalus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'aggiunta');
      }
    } catch (err) {
      toast.error('Errore di rete');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditBonusMalus = async () => {
    if (!editName.trim() || !editPoints) {
      toast.error('Inserisci tutti i campi');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/bonus-malus/${itemToEdit.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editName,
          points: parseInt(editPoints),
          category: editCategory,
          type: editType,
        }),
      });

      if (response.ok) {
        toast.success('Bonus/Malus modificato!');
        setShowEditDialog(false);
        setItemToEdit(null);
        fetchBonusMalus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nella modifica');
      }
    } catch (err) {
      toast.error('Errore di rete');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBonusMalus = async (itemId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo bonus/malus?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/bonus-malus/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Bonus/Malus eliminato');
        fetchBonusMalus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'eliminazione');
      }
    } catch (err) {
      toast.error('Errore di rete');
    }
  };

  const handleResetBonusMalus = async (itemId, itemName) => {
    if (!window.confirm(`Sei sicuro di voler resettare "${itemName}" ai valori default originali?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/leagues/${leagueId}/bonus-malus/${itemId}/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.reset) {
          toast.success('Bonus/Malus resettato ai valori default');
        } else {
          toast.info('Nessuna modifica da resettare');
        }
        fetchBonusMalus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel reset');
      }
    } catch (err) {
      toast.error('Errore di rete');
    }
  };

  const openEditDialog = (item) => {
    setItemToEdit(item);
    setEditName(item.name);
    setEditPoints(item.points.toString());
    setEditCategory(item.category);
    setEditType(item.type);
    setShowEditDialog(true);
  };

  // Group by type and category
  const bonusItems = bonusMalus.filter(item => item.type === 'bonus');
  const malusItems = bonusMalus.filter(item => item.type === 'malus');

  const groupByCategory = (items) => {
    const grouped = {};
    items.forEach(item => {
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    });
    return grouped;
  };

  const handleDownloadPDF = () => {
    try {
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      doc.text('Bonus e Malus', 14, 20);
      
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(leagueName || 'Lega', 14, 28);
      
      // Separator line
      doc.setDrawColor(200, 200, 200);
      doc.line(14, 32, 196, 32);
      
      let yPosition = 40;
      
      // BONUS Section
      const groupedBonus = groupByCategory(bonusItems);
      if (Object.keys(groupedBonus).length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(22, 163, 74); // Green color
        doc.text('BONUS', 14, yPosition);
        yPosition += 8;
        
        Object.entries(groupedBonus).forEach(([category, items]) => {
          // Category header
          doc.setFontSize(11);
          doc.setTextColor(60, 60, 60);
          doc.text(category.toUpperCase(), 14, yPosition);
          yPosition += 5;
          
          // Table for this category
          const bonusData = items.map(item => [
            item.name,
            item.is_custom ? 'Custom' : 'Default',
            `+${item.points}`
          ]);
          
          autoTable(doc, {
            startY: yPosition,
            head: [['Nome', 'Tipo', 'Punti']],
            body: bonusData,
            theme: 'striped',
            headStyles: { fillColor: [22, 163, 74], fontSize: 10 },
            bodyStyles: { fontSize: 9 },
            margin: { left: 14, right: 14 },
            tableWidth: 'auto',
            columnStyles: {
              0: { cellWidth: 100 },
              1: { cellWidth: 40 },
              2: { cellWidth: 42, halign: 'right' }
            }
          });
          
          yPosition = doc.lastAutoTable.finalY + 10;
        });
      }
      
      // Check if we need a new page
      if (yPosition > 200) {
        doc.addPage();
        yPosition = 20;
      }
      
      // MALUS Section
      const groupedMalus = groupByCategory(malusItems);
      if (Object.keys(groupedMalus).length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(220, 38, 38); // Red color
        doc.text('MALUS', 14, yPosition);
        yPosition += 8;
        
        Object.entries(groupedMalus).forEach(([category, items]) => {
          // Category header
          doc.setFontSize(11);
          doc.setTextColor(60, 60, 60);
          doc.text(category.toUpperCase(), 14, yPosition);
          yPosition += 5;
          
          // Table for this category
          const malusData = items.map(item => [
            item.name,
            item.is_custom ? 'Custom' : 'Default',
            `${item.points}`
          ]);
          
          autoTable(doc, {
            startY: yPosition,
            head: [['Nome', 'Tipo', 'Punti']],
            body: malusData,
            theme: 'striped',
            headStyles: { fillColor: [220, 38, 38], fontSize: 10 },
            bodyStyles: { fontSize: 9 },
            margin: { left: 14, right: 14 },
            tableWidth: 'auto',
            columnStyles: {
              0: { cellWidth: 100 },
              1: { cellWidth: 40 },
              2: { cellWidth: 42, halign: 'right' }
            }
          });
          
          yPosition = doc.lastAutoTable.finalY + 10;
        });
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        const footerText = `Generato il ${new Date().toLocaleDateString('it-IT')} - Pagina ${i} di ${pageCount}`;
        doc.text(footerText, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, { align: 'center' });
      }
      
      // Save the PDF
      const fileName = `bonus-malus-${leagueName?.replace(/\s+/g, '-').toLowerCase() || 'lega'}-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast.success('PDF scaricato con successo!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Errore nella generazione del PDF');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-semibold">
            {isAdmin ? 'Gestione Punti' : 'Bonus e Malus'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {isAdmin 
              ? 'Personalizza i valori dei bonus e malus' 
              : 'Visualizza i bonus e malus della lega'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleDownloadPDF} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Scarica PDF
          </Button>
          {isAdmin && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Aggiungi
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Aggiungi Bonus/Malus</DialogTitle>
                  <DialogDescription>
                    Crea un nuovo bonus o malus personalizzato
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={newType} onValueChange={setNewType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bonus">Bonus</SelectItem>
                        <SelectItem value="malus">Malus</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select value={newCategory} onValueChange={setNewCategory}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input 
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Es: Interrogazione perfetta"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Punti</Label>
                    <Input 
                      type="number"
                      value={newPoints}
                      onChange={(e) => setNewPoints(e.target.value)}
                      placeholder="Es: 10"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                    Annulla
                  </Button>
                  <Button onClick={handleAddBonusMalus} disabled={isSubmitting}>
                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aggiungi'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Tabs for Bonus and Malus */}
      <Tabs defaultValue="bonus" className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-muted p-1 rounded-lg">
          <TabsTrigger value="bonus" className="flex items-center justify-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md">
            <TrendingUp className="h-4 w-4" />
            Bonus ({bonusItems.length})
          </TabsTrigger>
          <TabsTrigger value="malus" className="flex items-center justify-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-md">
            <TrendingDown className="h-4 w-4" />
            Malus ({malusItems.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bonus" className="mt-6">
          <ScrollArea className="h-[500px]">
            <div className="space-y-4">
              {Object.entries(groupByCategory(bonusItems)).map(([category, items]) => (
                <Card key={category}>
                  <CardHeader>
                    <CardTitle className="text-base capitalize">{category}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {items.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-3 border rounded">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{item.name}</span>
                              {item.is_custom && (
                                <Badge variant="outline" className="text-xs">Custom</Badge>
                              )}
                            </div>
                            <span className="text-sm text-green-600 font-semibold">+{item.points} punti</span>
                          </div>
                          {isAdmin && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openEditDialog(item)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleResetBonusMalus(item.id, item.name)}
                                title="Reset ai valori default"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeleteBonusMalus(item.id)}
                                title={item.is_custom ? "Elimina" : "Nascondi dalla lega"}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="malus" className="mt-6">
          <ScrollArea className="h-[500px]">
            <div className="space-y-4">
              {Object.entries(groupByCategory(malusItems)).map(([category, items]) => (
                <Card key={category}>
                  <CardHeader>
                    <CardTitle className="text-base capitalize">{category}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {items.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-3 border rounded">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{item.name}</span>
                              {item.is_custom && (
                                <Badge variant="outline" className="text-xs">Custom</Badge>
                              )}
                            </div>
                            <span className="text-sm text-red-600 font-semibold">{item.points} punti</span>
                          </div>
                          {isAdmin && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openEditDialog(item)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleResetBonusMalus(item.id, item.name)}
                                title="Reset ai valori default"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeleteBonusMalus(item.id)}
                                title={item.is_custom ? "Elimina" : "Nascondi dalla lega"}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Bonus/Malus</DialogTitle>
            <DialogDescription>
              Modifica tutti i campi del bonus/malus selezionato
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonus">Bonus</SelectItem>
                  <SelectItem value="malus">Malus</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={editCategory} onValueChange={setEditCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Nome</Label>
              <Input 
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Es: Interrogazione perfetta"
              />
            </div>

            <div className="space-y-2">
              <Label>Punti</Label>
              <Input 
                type="number"
                value={editPoints}
                onChange={(e) => setEditPoints(e.target.value)}
                placeholder="Es: 10"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleEditBonusMalus} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
